import { Fragment, useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  ArrowLeft, Loader2, ChevronRight, ChevronDown, Maximize2, X, ExternalLink, Workflow, Layers,
  Globe2, Shirt, Grid3x3, Scale, Lock, Building2, Search, Columns3, Link2, MapPin, Target,
  BookOpen,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  AttrValue, StageBadge, ScopeBadge, isBlank, isClientProfile, categoryStyle, formatQuantity, CountryLabel, CountryFlag, resolveCountry,
} from '../components/process/processUi';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return format(new Date(value), 'dd MMM yyyy');
  } catch {
    return '—';
  }
};

// Joins hierarchy values ("Apparel › Tops"); all-wildcard collapses to "Any".
const Path = ({ values }) => {
  const parts = values.filter(v => !isBlank(v));
  if (parts.length === 0) return <span className="text-muted-foreground">—</span>;
  if (parts.every(v => v === '*')) return <AttrValue value="*" />;
  return (
    <span className="whitespace-nowrap">
      {parts.map((v, i) => (
        <Fragment key={i}>
          {i > 0 && <span className="text-muted-foreground mx-1">›</span>}
          {v === '*' ? <span className="text-muted-foreground">Any</span> : v}
        </Fragment>
      ))}
    </span>
  );
};

const SourceLink = ({ url }) => (url ? (
  <a href={url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
    className="text-primary inline-flex items-center gap-1 hover:underline">
    Open <ExternalLink className="w-3 h-3" />
  </a>
) : <span className="text-muted-foreground">—</span>);

// Default view: curated, combined columns.
const COMPACT_COLUMNS = [
  { key: 'scope', label: 'Scope', render: (p) => <ScopeBadge profile={p} /> },
  {
    key: 'country', label: 'Country',
    render: (p) => <CountryLabel value={p.country} sub={!isBlank(p.continent) && p.continent !== '*' ? p.continent : null} />,
  },
  { key: 'fibre_material', label: 'Fibre Material' },
  { key: 'formation_type', label: 'Formation' },
  { key: 'product', label: 'Product', render: (p) => <Path values={[p.product_class, p.product_sub_class, p.product_type]} /> },
  { key: 'colour', label: 'Colour' },
  { key: 'input_unit', label: 'Input Unit' },
  {
    key: 'source', label: 'Source',
    render: (p) => (isBlank(p.source_database) ? <span className="text-muted-foreground">—</span> : (
      <span className="whitespace-nowrap">{p.source_database}
        {!isBlank(p.source_database_version) && <span className="text-muted-foreground"> v{p.source_database_version}</span>}
      </span>
    )),
  },
];

// Expanded view: every profile field.
const ALL_COLUMNS = [
  { key: 'scope', label: 'Scope', render: (p) => <ScopeBadge profile={p} /> },
  { key: 'continent', label: 'Continent' },
  { key: 'country', label: 'Country', render: (p) => <CountryLabel value={p.country} /> },
  { key: 'state', label: 'State' },
  { key: 'city', label: 'City' },
  { key: 'fibre_material', label: 'Fibre Material' },
  { key: 'formation_type', label: 'Formation Type' },
  { key: 'product_class', label: 'Product Class' },
  { key: 'product_sub_class', label: 'Product Sub Class' },
  { key: 'product_type', label: 'Product Type' },
  { key: 'colour', label: 'Colour' },
  { key: 'input_unit', label: 'Input Unit' },
  { key: 'waste_percentage', label: 'Waste %' },
  { key: 'source_database', label: 'Source Database' },
  { key: 'source_database_version', label: 'DB Version' },
  { key: 'source_database_release_year', label: 'DB Release Year' },
  { key: 'source_dataset_name', label: 'Dataset' },
  { key: 'source_dataset_reference_year', label: 'Dataset Year' },
  { key: 'source_unique_identifier', label: 'Source ID' },
  { key: 'source_url', label: 'Source URL', render: (p) => <SourceLink url={p.source_url} /> },
  { key: 'version', label: 'Version' },
  { key: 'created_by', label: 'Created By' },
  { key: 'reviewed_by', label: 'Reviewed By' },
  { key: 'created_at', label: 'Created At', render: (p) => formatDate(p.created_at) },
];

const ATTRIBUTE_FIELDS = [
  ['Continent', 'continent'], ['Country', 'country'], ['State', 'state'], ['City', 'city'],
  ['Fibre Material', 'fibre_material'], ['Formation Type', 'formation_type'],
  ['Product Class', 'product_class'], ['Product Sub Class', 'product_sub_class'],
  ['Product Type', 'product_type'], ['Colour', 'colour'], ['Waste %', 'waste_percentage'],
];

const SOURCE_FIELDS = [
  ['Database', 'source_database'], ['Version', 'source_database_version'],
  ['Release Year', 'source_database_release_year'], ['Dataset', 'source_dataset_name'],
  ['Dataset Year', 'source_dataset_reference_year'], ['Source ID', 'source_unique_identifier'],
  ['Profile Version', 'version'], ['Created By', 'created_by'], ['Reviewed By', 'reviewed_by'],
];

const activityName = (row) => row.type || row.sub_category || row.category || `Activity set #${row.activity_set_id}`;
const activityPath = (row) => [row.category, row.sub_category].filter(Boolean).join(' › ');

const ProcessDataList = ({ items, profile }) => {
  const outputUnit = isBlank(profile.input_unit) ? null : profile.input_unit;
  return (
  <div className="rounded-lg border bg-white">
    {outputUnit && (
      <div className="flex items-center gap-4 px-4 py-3 bg-primary/5 border-b border-primary/15 rounded-t-lg">
        <div className="w-10 h-10 rounded-lg bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0">
          <Target className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-primary/70">Output</p>
          <p className="text-sm text-muted-foreground truncate">
            All quantities below are per 1 {outputUnit} produced
          </p>
        </div>
        <p className="font-mono text-2xl font-bold text-primary whitespace-nowrap">
          1 <span className="text-base">{outputUnit}</span>
        </p>
      </div>
    )}
    <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-b bg-gray-50/60">
      <h4 className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
        Process data <span className="tabular-nums font-normal">· {items.length}</span>
      </h4>
      {!outputUnit && <span className="text-xs text-muted-foreground">Output unit not recorded</span>}
    </div>
    <ul className="divide-y">
      {items.map((row) => {
        const { icon: Icon, tone, kind } = categoryStyle(row);
        return (
          <li key={row.process_data_id} className="flex items-center gap-4 px-4 py-3 hover:bg-gray-50 transition-colors">
            <div className={`w-10 h-10 rounded-lg ${tone} flex items-center justify-center flex-shrink-0`} title={kind}>
              <Icon className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                <p className="font-medium text-sm truncate" title={[activityName(row), row.sub_type].filter(Boolean).join(' · ')}>
                  {activityName(row)}
                  {row.sub_type && <span className="text-muted-foreground font-normal"> · {row.sub_type}</span>}
                </p>
                {row.is_input_for_process && (
                  <Badge variant="outline" className="h-5 text-[11px] font-normal bg-emerald-50 text-emerald-800 border-emerald-200 flex-shrink-0">
                    Material input
                  </Badge>
                )}
                {row.is_dummy_activity_set && (
                  <Badge variant="outline" className="h-5 text-[11px] font-normal border-dashed flex-shrink-0">Dummy</Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground truncate mt-0.5" title={activityPath(row)}>
                {activityPath(row) || '—'}
                <span className="font-mono"> · AS #{row.activity_set_id}</span>
                {!row.is_direct_activity_set && <span> · indirect</span>}
              </p>
            </div>
            <span className="hidden sm:inline text-xs text-muted-foreground w-28 text-right flex-shrink-0">{kind}</span>
            <div className="text-right flex-shrink-0 w-28 whitespace-nowrap">
              <span className="font-mono text-base font-semibold tabular-nums">{formatQuantity(row.quantity)}</span>
              <span className="text-xs text-muted-foreground ml-1">{row.unit}</span>
            </div>
          </li>
        );
      })}
    </ul>
  </div>
  );
};

const KeyValueList = ({ fields, profile }) => (
  <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
    {fields.map(([label, key]) => (
      <div key={key} className="min-w-0">
        <dt className="text-xs text-muted-foreground">{label}</dt>
        <dd className="truncate" title={isBlank(profile[key]) ? '' : String(profile[key])}>
          {key === 'country' ? <CountryLabel value={profile[key]} /> : <AttrValue value={profile[key]} />}
        </dd>
      </div>
    ))}
  </dl>
);

const ProfilePanel = ({ profile, state }) => {
  const items = state?.items || [];
  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 items-start">
      <div className="xl:col-span-2">
        {!state || state.loading ? (
          <Skeleton className="h-48 w-full" />
        ) : state.error ? (
          <p className="text-sm text-destructive rounded-lg border bg-white p-4">{state.error}</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground rounded-lg border bg-white p-4">No process data for this profile</p>
        ) : (
          <ProcessDataList items={items} profile={profile} />
        )}
      </div>

      <div className="space-y-4">
        <div className="rounded-lg border bg-white p-4">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3 flex items-center gap-2">
            <MapPin className="w-3.5 h-3.5" /> Matching attributes
          </h4>
          <KeyValueList fields={ATTRIBUTE_FIELDS} profile={profile} />
        </div>
        <div className="rounded-lg border bg-white p-4">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3 flex items-center justify-between gap-2">
            <span className="flex items-center gap-2"><BookOpen className="w-3.5 h-3.5" /> Source</span>
            <SourceLink url={profile.source_url} />
          </h4>
          <KeyValueList fields={SOURCE_FIELDS} profile={profile} />
          <div className="mt-3 pt-3 border-t">
            <label htmlFor={`additional-info-${profile.process_activity_profile_id}`} className="text-xs text-muted-foreground">
              Additional Information
            </label>
            <Textarea
              id={`additional-info-${profile.process_activity_profile_id}`}
              readOnly
              value={profile.additional_information || ''}
              placeholder="No additional information"
              rows={4}
              className="mt-1 text-sm bg-gray-50 resize-y focus-visible:ring-1"
              data-testid={`additional-info-${profile.process_activity_profile_id}`}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const ProfilesTable = ({ profiles, columns, expandedRows, onToggle, profileData }) => {
  const colSpan = columns.length + 3;
  return (
    <Table>
      <TableHeader>
        <TableRow className="bg-gray-50 table-dense">
          <TableHead className="w-8" />
          <TableHead className="whitespace-nowrap">Profile</TableHead>
          {columns.map(c => <TableHead key={c.key} className="whitespace-nowrap">{c.label}</TableHead>)}
          <TableHead className="text-right whitespace-nowrap">Data Rows</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {profiles.map((p) => {
          const pid = p.process_activity_profile_id;
          const isOpen = expandedRows.has(pid);
          return (
            <Fragment key={pid}>
              <TableRow
                className={`cursor-pointer ${isOpen ? 'bg-primary/5 hover:bg-primary/5' : 'hover:bg-gray-50'}`}
                onClick={() => onToggle(pid)}
                data-testid={`profile-row-${pid}`}
              >
                <TableCell className="w-8">
                  {isOpen
                    ? <ChevronDown className="w-4 h-4 text-primary" />
                    : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                </TableCell>
                <TableCell className="font-mono text-sm">#{pid}</TableCell>
                {columns.map(c => (
                  <TableCell key={c.key} className="whitespace-nowrap">
                    {c.render ? c.render(p) : <AttrValue value={p[c.key]} />}
                  </TableCell>
                ))}
                <TableCell className="text-right">
                  <span className={`inline-flex items-center justify-center min-w-[2rem] h-7 px-2 rounded-md font-semibold tabular-nums ${
                    p.data_count > 0 ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-muted-foreground'
                  }`}>
                    {p.data_count}
                  </span>
                </TableCell>
              </TableRow>
              {isOpen && (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={colSpan} className="bg-gray-50/80 p-4 border-l-2 border-l-primary">
                    <ProfilePanel profile={p} state={profileData[pid]} />
                  </TableCell>
                </TableRow>
              )}
            </Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
};

const StatTile = ({ label, value, sub, icon: Icon, tone }) => (
  <Card className="border-gray-200">
    <CardContent className="p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg ${tone} flex items-center justify-center flex-shrink-0`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <p className="text-2xl font-heading font-bold tabular-nums leading-tight">{value}</p>
        <p className="text-xs text-muted-foreground truncate" title={sub || label}>{sub || label}</p>
      </div>
    </CardContent>
  </Card>
);

const WEIGHT_ATTRIBUTE_LABELS = {
  greenstitch_id: 'Client (GS ID)',
  parent_greenstitch_id: 'Parent client (GS ID)',
  fibre_material: 'Fibre Material',
  formation_type: 'Formation Type',
  product_type: 'Product Type',
  country: 'Country',
  colour: 'Colour',
  continent: 'Continent',
};

// Lower weight = higher priority: weight 1 is compared first when matching a profile.
// Items arrive sorted by weight ascending, so chain position is the priority rank.
// Chips fade from solid primary (highest) to a light tint (lowest).
const chipAlpha = (i, n) => (n <= 1 ? 1 : 1 - (0.82 * i) / (n - 1));

const PriorityChain = ({ items }) => {
  const active = items.filter(w => w.is_active);
  const inactive = items.filter(w => !w.is_active);
  const label = (w) => (
    <>
      {WEIGHT_ATTRIBUTE_LABELS[w.attribute_name] || w.attribute_name}
      {!isBlank(w.attribute_value) && <span className="opacity-75"> = {w.attribute_value}</span>}
    </>
  );

  return (
    <div className="space-y-4">
      <div className="mx-auto max-w-sm">
        <p className="text-center text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Highest priority</p>
        <ol className="flex flex-col items-stretch" aria-label="Attributes from highest to lowest priority">
          {active.map((w, i) => {
            const alpha = chipAlpha(i, active.length);
            const dark = alpha > 0.5;
            return (
              <li key={w.id} className="flex flex-col items-stretch">
                {i > 0 && <ChevronDown className="w-4 h-4 text-muted-foreground mx-auto my-0.5" aria-hidden="true" />}
                <span
                  className={`flex items-center gap-2 h-10 pl-1.5 pr-3 rounded-full border text-sm font-medium ${
                    dark ? 'text-primary-foreground border-transparent' : 'text-primary border-primary/20'
                  }`}
                  style={{ backgroundColor: `hsl(var(--primary) / ${alpha})` }}
                  title={`Priority ${i + 1} · weight ${w.weight} · ${w.is_wild_card_allowed ? 'can match "Any"' : 'exact match only'}`}
                >
                  <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold tabular-nums flex-shrink-0 ${
                    dark ? 'bg-white/20' : 'bg-white text-primary'
                  }`}>
                    {i + 1}
                  </span>
                  <span className="flex-1 min-w-0 truncate">{label(w)}</span>
                  {!w.is_wild_card_allowed && <Lock className="w-3.5 h-3.5 opacity-80 flex-shrink-0" aria-label="Exact match only" />}
                </span>
              </li>
            );
          })}
        </ol>
        <p className="text-center text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mt-2">Lowest priority</p>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pt-3 border-t text-xs text-muted-foreground">
        <span className="flex items-center gap-1"><Lock className="w-3 h-3" /> Exact match only (can't be "Any")</span>
        <span>Number = priority rank · lower weight = higher priority</span>
        <span>Hover a chip for its weight</span>
      </div>

      {inactive.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Not used (inactive): {inactive.map((w, i) => (
            <Fragment key={w.id}>{i > 0 && ', '}{label(w)}</Fragment>
          ))}
        </p>
      )}
    </div>
  );
};

// Global attribute weights used to match process activity profiles (attribute_weight, type PROCESS).
const AttributeWeightsDialog = ({ open, onOpenChange }) => {
  const [state, setState] = useState({ loading: false, error: null, items: null });

  useEffect(() => {
    if (!open || state.items || state.loading || state.error) return;
    setState({ loading: true, error: null, items: null });
    axios.get(`${API_URL}/processes/attribute-weights`)
      .then(({ data }) => setState({ loading: false, error: null, items: data.items || [] }))
      .catch(() => setState({ loading: false, error: 'Failed to load attribute weights', items: null }));
  }, [open, state.items, state.loading, state.error]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Scale className="w-5 h-5 text-primary" /> Attribute weights</DialogTitle>
          <DialogDescription>
            The order in which profile attributes are compared when picking the best-matching activity profile.
            This ranking applies to all processes.
          </DialogDescription>
        </DialogHeader>
        {state.loading || (!state.items && !state.error) ? (
          <div className="space-y-2">{[0, 1, 2, 3].map(i => <Skeleton key={i} className="h-9 w-full" />)}</div>
        ) : state.error ? (
          <div className="flex items-center justify-between gap-2 rounded-md border p-3 text-sm">
            <span className="text-destructive">{state.error}</span>
            <Button variant="outline" size="sm" onClick={() => setState({ loading: false, error: null, items: null })}>Retry</Button>
          </div>
        ) : state.items.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No process attribute weights configured</p>
        ) : (
          <PriorityChain items={state.items} />
        )}
      </DialogContent>
    </Dialog>
  );
};

// Distinct values of one profile attribute; '*' / blank collapse into an "Any" chip.
const MAX_CHIPS = 3;
const ValuesTile = ({ label, values, hasAny, icon: Icon, tone }) => {
  const list = [...values];
  const shown = list.slice(0, MAX_CHIPS);
  const extra = list.length - shown.length;
  return (
    <Card className="border-gray-200">
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg ${tone} flex items-center justify-center flex-shrink-0`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <p className="text-2xl font-heading font-bold tabular-nums leading-tight">{list.length}</p>
            <p className="text-xs text-muted-foreground truncate">{label}</p>
          </div>
          <div className="flex items-center gap-1 mt-1 flex-wrap" title={list.join(', ')}>
            {shown.map(v => (
              <Badge key={v} variant="secondary" className="h-5 px-1.5 text-[11px] font-normal max-w-[9rem] truncate">{v}</Badge>
            ))}
            {extra > 0 && <span className="text-[11px] text-muted-foreground">+{extra} more</span>}
            {hasAny && (
              <Badge variant="outline" className="h-5 px-1.5 text-[11px] font-normal text-muted-foreground border-dashed">Any</Badge>
            )}
            {list.length === 0 && !hasAny && <span className="text-xs text-muted-foreground">Not recorded</span>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const distinctValues = (profiles, key) => ({
  values: new Set(profiles.map(p => p[key]).filter(v => !isBlank(v) && v !== '*')),
  hasAny: profiles.some(p => p[key] === '*'),
});

const SCOPES = [
  { value: 'all', label: 'All' },
  { value: 'default', label: 'Secondary', icon: Globe2 },
  { value: 'client', label: 'Client-specific', icon: Building2 },
];

export default function ProcessDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [process, setProcess] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAllColumns, setShowAllColumns] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [weightsOpen, setWeightsOpen] = useState(false);
  const [profileSearch, setProfileSearch] = useState('');
  const [scope, setScope] = useState('all');
  const [expandedRows, setExpandedRows] = useState(() => new Set());
  // profileId -> { loading, error, items }
  const [profileData, setProfileData] = useState({});

  const fetchProcess = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/processes/${id}`);
      setProcess(data.process);
      setProfiles(data.profiles || []);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Process not found');
        navigate('/processes');
        return;
      }
      toast.error('Failed to load process');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    setExpandedRows(new Set());
    setProfileData({});
    setProfileSearch('');
    setScope('all');
    fetchProcess();
  }, [fetchProcess]);

  // Esc closes full screen; lock page scroll while open.
  useEffect(() => {
    if (!fullscreen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setFullscreen(false); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [fullscreen]);

  const loadProfileData = async (profileId) => {
    setProfileData(prev => ({ ...prev, [profileId]: { loading: true, error: null, items: [] } }));
    try {
      const { data } = await axios.get(`${API_URL}/processes/${id}/profiles/${profileId}/data`);
      setProfileData(prev => ({ ...prev, [profileId]: { loading: false, error: null, items: data.items || [] } }));
    } catch (error) {
      setProfileData(prev => ({ ...prev, [profileId]: { loading: false, error: 'Failed to load process data', items: [] } }));
    }
  };

  const toggleRow = (profileId) => {
    const opening = !expandedRows.has(profileId);
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (opening) next.add(profileId);
      else next.delete(profileId);
      return next;
    });
    if (opening && (!profileData[profileId] || profileData[profileId].error)) {
      loadProfileData(profileId);
    }
  };

  const stats = useMemo(() => {
    const countries = new Set(profiles.map(p => p.country).filter(c => !isBlank(c) && c !== '*'));
    const hasGlobal = profiles.some(p => isBlank(p.country) || p.country === '*');
    return {
      countries,
      hasGlobal,
      clientCount: profiles.filter(isClientProfile).length,
      materials: distinctValues(profiles, 'fibre_material'),
      formations: distinctValues(profiles, 'formation_type'),
    };
  }, [profiles]);

  const visibleProfiles = useMemo(() => {
    const q = profileSearch.trim().toLowerCase();
    return profiles.filter(p => {
      if (scope === 'default' && isClientProfile(p)) return false;
      if (scope === 'client' && !isClientProfile(p)) return false;
      if (!q) return true;
      return Object.values(p).some(v => v !== null && v !== undefined && String(v).toLowerCase().includes(q));
    });
  }, [profiles, profileSearch, scope]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-36 w-full" />
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {[0, 1, 2, 3].map(i => <Skeleton key={i} className="h-20" />)}
          </div>
          <Skeleton className="h-64 w-full" />
        </div>
      </DashboardLayout>
    );
  }

  if (!process) {
    return (
      <DashboardLayout>
        <div className="text-center py-24">
          <p className="text-muted-foreground mb-4">Process could not be loaded</p>
          <Button variant="outline" onClick={fetchProcess}>Retry</Button>
        </div>
      </DashboardLayout>
    );
  }

  const toolbar = (
    <div className="flex flex-col md:flex-row md:items-center gap-3">
      <div className="relative flex-1 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Filter profiles by any value…"
          value={profileSearch}
          onChange={(e) => setProfileSearch(e.target.value)}
          className="pl-10 h-9 input-eco"
          data-testid="profile-search-input"
        />
      </div>
      <div className="inline-flex rounded-md border bg-gray-50 p-0.5" role="group">
        {SCOPES.map(({ value, label, icon: Icon }) => (
          <button
            key={value}
            type="button"
            onClick={() => setScope(value)}
            className={`inline-flex items-center gap-1.5 px-3 h-8 rounded text-sm transition-colors ${
              scope === value ? 'bg-white shadow-sm text-foreground font-medium' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {Icon && <Icon className="w-3.5 h-3.5" />}{label}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2 md:ml-auto">
        {!fullscreen && (
          <Button variant="outline" size="sm" className="gap-2" onClick={() => setWeightsOpen(true)} data-testid="attribute-weights-btn">
            <Scale className="w-4 h-4" />
            Weights
          </Button>
        )}
        {!fullscreen && (
          <Button variant="outline" size="sm" className="gap-2" onClick={() => setShowAllColumns(v => !v)} data-testid="toggle-columns-btn">
            <Columns3 className="w-4 h-4" />
            {showAllColumns ? 'Key columns' : 'All columns'}
          </Button>
        )}
        <Button
          variant={fullscreen ? 'default' : 'outline'}
          size="sm"
          className="gap-2"
          onClick={() => setFullscreen(v => !v)}
          data-testid="expand-table-btn"
        >
          {fullscreen ? <X className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          {fullscreen ? 'Close' : 'Expand'}
        </Button>
      </div>
    </div>
  );

  const table = visibleProfiles.length === 0 ? (
    <div className="text-center py-12">
      <p className="text-muted-foreground">
        {profiles.length === 0 ? 'No profiles for this process' : 'No profiles match these filters'}
      </p>
    </div>
  ) : (
    <ProfilesTable
      profiles={visibleProfiles}
      columns={fullscreen || showAllColumns ? ALL_COLUMNS : COMPACT_COLUMNS}
      expandedRows={expandedRows}
      onToggle={toggleRow}
      profileData={profileData}
    />
  );

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="process-detail-page">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate('/processes')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <Link to="/processes" className="hover:text-foreground">Processes</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <span className="text-foreground font-medium truncate">{process.process_name}</span>
        </div>

        {/* Hero */}
        <Card className="border-gray-200 overflow-hidden">
          <CardContent className="p-0">
            <div className="p-6 flex flex-col md:flex-row md:items-start gap-5">
              <div className="w-14 h-14 rounded-xl bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0">
                <Workflow className="w-7 h-7" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4">
                  <h1 className="font-heading text-3xl font-bold tracking-tight break-words">{process.process_name}</h1>
                  <span className="font-mono text-sm text-muted-foreground flex-shrink-0 mt-2">#{process.process_id}</span>
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  <Badge variant="outline" className="font-normal bg-white">
                    <span className="text-muted-foreground mr-1">Type</span>
                    <span className="font-medium">{process.process_type || '—'}</span>
                  </Badge>
                  <Badge variant="outline" className="font-normal bg-white">
                    <span className="text-muted-foreground mr-1">Sub Type</span>
                    <span className="font-medium">{process.process_sub_type || '—'}</span>
                  </Badge>
                  <StageBadge stage={process.lifecycle_stage} />
                  {!isBlank(process.tier) && <Badge variant="secondary" className="font-normal">Tier {process.tier}</Badge>}
                  {process.equivalent_process_id && (
                    <Link
                      to={`/processes/${process.equivalent_process_id}`}
                      title={[process.equivalent_process_name, process.equivalent_process_type, process.equivalent_process_sub_type].filter(Boolean).join(' · ')}
                      data-testid="equivalent-process-link"
                    >
                      <Badge variant="outline" className="font-normal bg-white hover:bg-primary/5 transition-colors">
                        <Link2 className="w-3 h-3 mr-1 text-muted-foreground" />
                        <span className="text-muted-foreground mr-1">Equivalent</span>
                        <span className="font-medium text-primary">
                          {process.equivalent_process_name}{process.equivalent_process_type ? ` · ${process.equivalent_process_type}` : ''}
                        </span>
                      </Badge>
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <StatTile label="Profiles" value={profiles.length} icon={Layers} tone="bg-primary/10 text-primary"
            sub={`${profiles.length - stats.clientCount} secondary · ${stats.clientCount} client`} />
          <Card className="border-gray-200">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-sky-100 text-sky-700 flex items-center justify-center flex-shrink-0">
                <Globe2 className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl font-heading font-bold tabular-nums leading-tight">{stats.countries.size}</p>
                {stats.countries.size ? (
                  <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                    {[...stats.countries].map(c => {
                      const country = resolveCountry(c);
                      return <span key={c} title={country.name}><CountryFlag code={country.code} /></span>;
                    })}
                    {stats.hasGlobal && <span className="text-xs text-muted-foreground ml-1">+ global</span>}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">{profiles.length ? 'Global (any country)' : 'No profiles'}</p>
                )}
              </div>
            </CardContent>
          </Card>
          <ValuesTile label={stats.materials.values.size === 1 ? 'Fibre material' : 'Fibre materials'}
            values={stats.materials.values} hasAny={stats.materials.hasAny}
            icon={Shirt} tone="bg-rose-100 text-rose-700" />
          <ValuesTile label={stats.formations.values.size === 1 ? 'Formation type' : 'Formation types'}
            values={stats.formations.values} hasAny={stats.formations.hasAny}
            icon={Grid3x3} tone="bg-violet-100 text-violet-700" />
        </div>

        {/* Profiles */}
        <Card className="border-gray-200">
          <CardContent className="p-0">
            <div className="p-4 border-b space-y-3">
              <div className="flex items-baseline justify-between gap-2">
                <h2 className="font-heading text-lg font-semibold">Activity profiles</h2>
                <p className="text-sm text-muted-foreground">
                  {visibleProfiles.length === profiles.length
                    ? `${profiles.length} profile${profiles.length === 1 ? '' : 's'}`
                    : `${visibleProfiles.length} of ${profiles.length}`}
                  {' · '}click a row for its process data
                </p>
              </div>
              {profiles.length > 0 && toolbar}
            </div>
            <div className="overflow-x-auto">{table}</div>
          </CardContent>
        </Card>
      </div>

      <AttributeWeightsDialog open={weightsOpen} onOpenChange={setWeightsOpen} />

      {/* Full-screen profile table */}
      {fullscreen && (
        <div className="fixed inset-0 z-[60] bg-gray-50 flex flex-col animate-fade-in" role="dialog" aria-modal="true" aria-label="Activity profiles">
          <div className="bg-white border-b px-6 py-4 space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-primary text-primary-foreground flex items-center justify-center">
                <Workflow className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <h2 className="font-heading text-lg font-semibold truncate">{process.process_name} — activity profiles</h2>
                <p className="text-xs text-muted-foreground">
                  {[process.process_type, process.process_sub_type, process.lifecycle_stage].filter(Boolean).join(' · ')}
                  {' · '}all columns · press Esc to close
                </p>
              </div>
            </div>
            {toolbar}
          </div>
          <div className="flex-1 overflow-auto p-6">
            <div className="bg-white border rounded-md overflow-x-auto">{table}</div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
